import _ from 'lodash'
import { useMemo } from 'react'
import { useResizeDetector } from 'react-resize-detector'
import { Outlet } from 'react-router-dom'

import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'

import { FreshworkWidget } from '@/components/ui/widgets/FreshworkWidget/FreshWorkWidget'
import SubscriptionChecker from '@/components/layout/SubscriptionChecker'
import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'
import { OfflineStatusIndicator } from '@/shared/offline/ui/OfflineStatusIndicator'
import { AbilityProvider } from '@/providers/AbilityProvider'

import { useGetLicensesQuery } from '@/core/services/license'
import { ExternalService } from '@/constants/external-services'
import { useGetTimeTrackingConsentQuery } from '@/core/api'

import { SidebarType } from '@/utils/subscription/types'
import { Role } from '@/core/types/shared'

import { useEmergencyRoomAlerts } from '@/hooks/useEmergencyRoomAlerts'
import { usePendingOrdersBadge } from '@/hooks/usePendingOrdersBadge'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAppSettings } from '@/redux/reducers/app.reducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { Header } from './Header'
import { Footer } from './Footer'
import { Sidebar } from './Sidebar'
import { sidebarItemsMap } from '../model/sidebarItems'
import { useLanguage } from '@/hooks/useLanguage'
import { ElaraPanelContent } from '@/components/elara/ElaraPanel'
import { useElaraPanel } from '@/providers/elara/ElaraPanelContext'

export type MainLayoutProps = {}

export function MainLayout(props: MainLayoutProps) {
  const { width: pageContainerWidth, ref: pageContainerRef } = useResizeDetector()
  const theme = useTheme()
  const authUser = useAppSelector(selectAuthUser)
  const appSettings = useAppSelector(selectAppSettings)
  const { data: licenses, isLoading: isLoadingLicenses } = useGetLicensesQuery({ id: authUser?.licenses }, { skip: !authUser?.licenses.length })
  const lang = useLanguage()

  const pendingOrdersBadge = usePendingOrdersBadge()
  // SEHA (en-SA): sound + visual alert for doctors when a new ER request arrives.
  useEmergencyRoomAlerts()
  const { data: timeTrackingConsent } = useGetTimeTrackingConsentQuery(undefined, { skip: !authUser })

  const hasElaraLicense = useMemo(() => {
    const allServices = _.uniq(_.flatten(licenses?.map((license) => license.services)))
    return allServices.includes(ExternalService.elaraRealtime)
  }, [licenses])

  const sidebarItems = useMemo(() => {
    const allowedSidebarComponents = _.uniq(_.flatten(licenses?.map((license) => license.components)))
      .filter((component) => component.startsWith('sidebar:'))
      .map((component) => component.replace('sidebar:', '')) as SidebarType[]

    // Show the unified Elara agent sidebar entry whenever the elara-realtime service is licensed,
    // even if the license component list doesn't explicitly include sidebar:medconnect-agent.
    if (hasElaraLicense && !allowedSidebarComponents.includes(SidebarType.MedConnectAgent)) {
      allowedSidebarComponents.push(SidebarType.MedConnectAgent)
    }

    // Tutorials is help content — always visible to every authenticated user,
    // regardless of license components.
    if (!allowedSidebarComponents.includes(SidebarType.Tutorials)) {
      allowedSidebarComponents.push(SidebarType.Tutorials)
    }

    const items = _.compact(allowedSidebarComponents.map((component) => sidebarItemsMap[component]).flat())

    const isAdmin = authUser?.roles?.includes(Role.Admin) ?? false
    const showTimeTracking = isAdmin || timeTrackingConsent?.enabled

    if (showTimeTracking) {
      const timeTrackingItem = sidebarItemsMap[SidebarType.TimeTracking]
      if (timeTrackingItem && !Array.isArray(timeTrackingItem)) {
        items.push({ ...timeTrackingItem, link: isAdmin ? '/admin/time-tracking' : '/time-tracking' })
      }
    }

    return items.map((item) => {
      if (item.name === SidebarType.MeetingLinks) {
        return { ...item, badge: pendingOrdersBadge }
      }
      return item
    })
  }, [licenses, hasElaraLicense, pendingOrdersBadge, timeTrackingConsent, authUser])

  const freshworkCustomer = useMemo(() => ({ email: authUser?.email ?? '', name: authUser?.userInfo.name + ' ' + authUser?.userInfo.surname }), [authUser])
  const elaraPanel = useElaraPanel()
  const isPanelOpen = elaraPanel?.isPanelOpen ?? false

  return (
    <AbilityProvider>
      <Box ref={pageContainerRef} sx={{ height: '100vh', overflow: 'hidden', backgroundColor: theme.palette.background.default, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} sx={{ height: '100%' }}>
          {(pageContainerWidth || 0) >= theme.breakpoints.values.md && <Sidebar isLoading={isLoadingLicenses} menuItems={sidebarItems} />}
          <Stack sx={{ flex: 1, position: 'relative', minWidth: 0, height: '100%', overflow: 'auto' }}>
            <Header menuItems={sidebarItems} />
            {appSettings.showFreshworkWidget && <FreshworkWidget customer={freshworkCustomer} language={lang === 'en-US' || lang === 'en-SA' ? 'en' : lang === 'ar-SA' ? 'ar' : lang} />}
            {isLoadingLicenses ? (
              <Stack flex={1} alignItems="center" justifyContent="center">
                <HeartbeatLoader sx={{ transform: 'scale(4)' }} />
              </Stack>
            ) : (
              <SubscriptionChecker subscriptionSidebarItems={sidebarItems}>
                <Outlet />
              </SubscriptionChecker>
            )}
            <Footer />
          </Stack>
          {isPanelOpen && hasElaraLicense && <ElaraPanelContent />}
        </Stack>
      </Box>
    </AbilityProvider>
  )
}
